import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { reportsAPI, invoicesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, Users, CreditCard, Clock, Download, FileSpreadsheet, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [paymentReport, setPaymentReport] = useState(null);
  const [overdueInvoices, setOverdueInvoices] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, paymentsRes, overdueRes] = await Promise.all([
        reportsAPI.getSummary(),
        reportsAPI.getPayments(),
        invoicesAPI.getAll({ status: 'overdue' })
      ]);
      
      setSummary(summaryRes.data);
      setPaymentReport(paymentsRes.data);
      setOverdueInvoices(overdueRes.data || []);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to export data');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/export/${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`${type}.csv downloaded`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    }
  };

  const handleExportAll = async () => {
    const exportTypes = ['customers', 'invoices', 'payments', 'leads', 'communications'];
    toast.info('Starting export of all data...');
    
    for (const type of exportTypes) {
      await handleExport(type);
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
    }
    
    toast.success('All data exported!');
  };

  // Calculate overdue amount
  const overdueAmount = overdueInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-gray-200 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-500 mt-1">Financial overview and analytics</p>
        </div>
      </div>

      {/* Export Section */}
      <Card className="border-0 shadow-sm bg-lime-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-lime-600" />
            Export Data to Spreadsheet
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 text-sm mb-4">
            Download your data as CSV files that can be opened in Excel, Google Sheets, or any spreadsheet application.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={handleExportAll}
              className="bg-lime-500 hover:bg-lime-600 text-black"
              data-testid="export-all-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export All Data
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleExport('customers')}
              className="border-lime-500 text-lime-700 hover:bg-lime-100"
              data-testid="export-customers-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Customers
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleExport('invoices')}
              className="border-lime-500 text-lime-700 hover:bg-lime-100"
              data-testid="export-invoices-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Invoices
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleExport('payments')}
              className="border-lime-500 text-lime-700 hover:bg-lime-100"
              data-testid="export-payments-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Payments
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleExport('leads')}
              className="border-lime-500 text-lime-700 hover:bg-lime-100"
              data-testid="export-leads-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Leads
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleExport('communications')}
              className="border-lime-500 text-lime-700 hover:bg-lime-100"
              data-testid="export-comms-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Communications
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Revenue Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-lime-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-lime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">${(summary?.total_revenue || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">This Month</p>
                <p className="text-2xl font-bold text-gray-900">${(summary?.monthly_revenue || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Outstanding</p>
                <p className="text-2xl font-bold text-gray-900">${(summary?.pending_amount || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600">${overdueAmount.toFixed(2)}</p>
                <p className="text-xs text-gray-400">{overdueInvoices.length} invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue Invoices Section */}
      {overdueInvoices.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Overdue Invoices ({overdueInvoices.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Due Date</th>
                    <th>Days Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueInvoices.map((invoice) => {
                    const dueDate = new Date(invoice.due_date);
                    const today = new Date();
                    const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
                    
                    return (
                      <tr key={invoice.id}>
                        <td className="font-medium">{invoice.invoice_number}</td>
                        <td>{invoice.customer_name}</td>
                        <td className="text-red-600 font-bold">${invoice.amount.toFixed(2)}</td>
                        <td>{new Date(invoice.due_date).toLocaleDateString()}</td>
                        <td>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            daysOverdue > 30 ? 'bg-red-200 text-red-800' :
                            daysOverdue > 14 ? 'bg-orange-200 text-orange-800' :
                            'bg-yellow-200 text-yellow-800'
                          }`}>
                            {daysOverdue} days
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Method Breakdown */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Payment Methods</CardTitle>
        </CardHeader>
        <CardContent>
          {!paymentReport?.by_method || Object.keys(paymentReport.by_method).length === 0 ? (
            <p className="text-gray-500 text-center py-4">No payment data available</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(paymentReport.by_method).map(([method, data]) => (
                <div key={method} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                    <span className="font-medium capitalize">{method.replace('_', ' ')}</span>
                  </div>
                  <p className="text-2xl font-bold text-lime-600">${data.total.toFixed(2)}</p>
                  <p className="text-sm text-gray-500">{data.count} payments</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Payment Performance */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Customer Payment Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {!paymentReport?.customer_stats || paymentReport.customer_stats.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No customer payment data available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Total Paid</th>
                    <th>Avg. Days to Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentReport.customer_stats.map((customer, index) => (
                    <tr key={index}>
                      <td className="font-medium">{customer.name}</td>
                      <td className="text-lime-600 font-bold">${customer.total.toFixed(2)}</td>
                      <td>
                        {customer.avg_days_to_pay !== null ? (
                          <span className={customer.avg_days_to_pay <= 7 ? 'text-green-600' : customer.avg_days_to_pay <= 14 ? 'text-yellow-600' : 'text-red-600'}>
                            {customer.avg_days_to_pay} days
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-gray-900">{summary?.total_customers || 0}</p>
            <p className="text-sm text-gray-500">Active Customers</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <TrendingUp className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-gray-900">{summary?.paid_invoices_count || 0}</p>
            <p className="text-sm text-gray-500">Paid Invoices</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-gray-900">{summary?.pending_invoices_count || 0}</p>
            <p className="text-sm text-gray-500">Pending Invoices</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-gray-900">{summary?.avg_days_to_payment || 0}</p>
            <p className="text-sm text-gray-500">Avg Days to Payment</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
